import { useCallback, useEffect, useMemo, useState } from "react";
import type { GenericTableColumn } from "./GenericTable";

export const getColumnId = <T,>(col: GenericTableColumn<T>): string =>
  col.id ?? (typeof col.header === "string" ? col.header : String(col.accessor ?? ""));

export const getColumnLabel = <T,>(col: GenericTableColumn<T>): string =>
  typeof col.header === "string" ? col.header : getColumnId(col);

interface UseColumnVisibilityResult<T> {
  visibleColumns: GenericTableColumn<T>[];
  isVisible: (id: string) => boolean;
  toggle: (id: string) => void;
  reset: () => void;
}

/**
 * Tracks which columns are visible, persisting hidden ids to localStorage.
 * Columns listed in `requiredIds` cannot be hidden.
 */
export function useColumnVisibility<T>(
  storageKey: string,
  allColumns: GenericTableColumn<T>[],
  requiredIds: string[] = [],
): UseColumnVisibilityResult<T> {
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return new Set();
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) return new Set();
      return new Set(
        (parsed as string[]).filter((id) => !requiredIds.includes(id)),
      );
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(Array.from(hiddenIds)));
    } catch {
      // ignore quota / privacy-mode failures
    }
  }, [storageKey, hiddenIds]);

  const visibleColumns = useMemo(
    () => allColumns.filter((col) => !hiddenIds.has(getColumnId(col))),
    [allColumns, hiddenIds],
  );

  const isVisible = useCallback((id: string) => !hiddenIds.has(id), [hiddenIds]);

  const toggle = useCallback(
    (id: string) => {
      if (requiredIds.includes(id)) return;
      setHiddenIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    [requiredIds],
  );

  const reset = useCallback(() => setHiddenIds(new Set()), []);

  return { visibleColumns, isVisible, toggle, reset };
}
